import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Mail } from "lucide-react";
import { toast, Toaster } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import { useSeo } from "@/lib/useSeo";
import { renderEmphasis } from "@/lib/richText";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [failed, setFailed] = useState(false);
  const [content, setContent] = useState({});

  useSeo({
    title: "Contact",
    description: "Write a letter to Gemma Veda — for press, festival invitations or a quiet hello.",
  });

  useEffect(() => {
    api.get("/settings").then(({ data }) => setContent(data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFailed(false);
    try {
      await api.post("/contact", { name, email, message });
      setSubmitted(true);
      setName(""); setEmail(""); setMessage("");
      toast.success("Letter sent. Thank you.");
    } catch (err) {
      setFailed(true);
      toast.error(formatApiError(err.response?.data?.detail, "Could not send your message."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PublicLayout>
      <Toaster position="top-center" />
      <section data-testid="contact-page" className="pt-36 pb-32 px-6 md:px-12 bg-[#FAFAFA] min-h-screen">
        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-16">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="md:col-span-5">
            <div
              data-testid="contact-letter-icon"
              className="w-14 h-14 rounded-full border border-[#C5A059] bg-[#FBF4E4] flex items-center justify-center"
            >
              <Mail className="w-6 h-6 text-[#C5A059]" strokeWidth={1.5} />
            </div>
            <div className="gv-label mt-6">Correspondence</div>
            <h1 className="font-serif text-5xl md:text-6xl mt-4 text-[#1A1F24] leading-[1.05]" data-testid="contact-heading">
              {renderEmphasis(content.contact_heading || "Write a *hand-typed letter* to Gemma.")}
            </h1>
            <p className="mt-6 text-[#5C6670] text-base md:text-lg font-light leading-relaxed" data-testid="contact-subtext">
              {content.contact_subtext || "For readers, press, festival invitations, or simply to say a sentence stayed with you — your note lands directly in Gemma's correspondence box, read by heart."}
            </p>
            <div className="mt-10 space-y-3 text-sm text-[#5C6670]">
              <div className="flex items-center gap-3">
                <span className="w-6 h-px bg-[#C5A059]" />
                <span className="tracking-[0.2em] uppercase text-xs" data-testid="contact-tagline">
                  {content.contact_tagline || "All replies are considered"}
                </span>
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15 }}
            className="md:col-span-7 space-y-8 bg-white px-8 py-12 md:px-12 md:py-16 border border-[#E5E5E5]"
          >
            <div>
              <label className="gv-label">Your name</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Marina Lestrange"
                className="gv-input mt-2"
                data-testid="contact-name"
              />
            </div>
            <div>
              <label className="gv-label">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="gv-input mt-2"
                data-testid="contact-email"
              />
            </div>
            <div>
              <label className="gv-label">Letter</label>
              <textarea
                required
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                placeholder="Dear Gemma…"
                className="gv-input mt-2 resize-none"
                data-testid="contact-message"
              />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <button type="submit" disabled={submitting} className="gv-btn-gold" data-testid="contact-submit">
                {submitting ? "Sending…" : "Send the letter"}
              </button>
              {submitted && (
                <span className="text-xs tracking-[0.2em] uppercase text-[#C5A059]" data-testid="contact-success">
                  ✓ Received with thanks
                </span>
              )}
            </div>
            {failed && (
              <p className="text-sm text-[#5C6670]" data-testid="contact-fallback">
                Didn't go through? Write directly instead —{" "}
                <a
                  href={`mailto:gemmavedaofficial@gmail.com?subject=A letter for Gemma&body=${encodeURIComponent(message)}`}
                  className="text-[#C5A059] gv-link"
                  data-testid="contact-fallback-mailto"
                >
                  email Gemma here
                </a>
                , so your words never go unread.
              </p>
            )}
          </motion.form>
        </div>
      </section>
    </PublicLayout>
  );
}
